import { instalacion } from './instalacion';

/**
 * El contrato de errores entre la base y las dos aplicaciones: cada regla levanta su excepción con
 * un código `TN_*` como prefijo del `message`, el único campo que PostgREST propaga. El cliente
 * traduce el código y nunca muestra el texto del servidor; el test cruza esta tabla con el baseline.
 */

/**
 * Frase que ve la persona, por código de dominio. Una entrada por código del esquema. `{nombre}` y
 * `{equipo}` se rellenan con la ficha de la instalación al entregar la frase (`traducirError`).
 */
export const MENSAJES_DOMINIO: Record<string, string> = {
  // --- sesión y permisos
  TN_REQUIERE_SESION: 'Tu sesión se cerró. Vuelve a entrar.',
  TN_SIN_MEMBRESIA: 'No tienes una membresía activa en ese negocio.',
  TN_SIN_PERMISO_VENTA: 'No tienes permiso para registrar ventas en este negocio.',
  TN_SIN_PERMISO_ANULAR: 'Solo el propietario puede anular comprobantes.',
  TN_SIN_PERMISO_GASTO: 'Solo el propietario registra, corrige o anula los gastos de este negocio.',
  TN_SIN_PERMISO_ABRIR_CAJA: 'No tienes permiso para abrir la caja de este negocio.',
  TN_SIN_PERMISO_REABRIR: 'No tienes permiso para reabrir un día cerrado en este negocio.',
  TN_REAPERTURA_SIN_MOTIVO: 'Para reabrir un día hay que decir por qué.',
  TN_CUADRE_CERRADO: 'Ese día está cerrado: para cambiarlo hay que reabrirlo primero.',
  TN_DIA_CERRADO: 'El día ya se cerró. Reábrelo en Cierre del día para seguir vendiendo.',
  TN_DIA_CERRADO_GASTO: 'El día ya se cerró. Reábrelo en Cierre del día para registrar el gasto.',
  TN_DIA_CERRADO_ANULAR: 'El día de esa venta ya se cerró. Reábrelo en Cierre del día para anularla.',
  TN_CUADRE_NO_REABRIBLE: 'Ese día no existe o no está cerrado.',
  TN_CUADRE_NO_ABIERTO: 'Ese día no se ha abierto.',
  TN_SIN_PERMISO_CORREGIR_BASE: 'No tienes permiso para corregir la base de caja en este negocio.',
  TN_CORRECCION_SIN_MOTIVO: 'Para corregir la base de caja hay que decir por qué.',
  TN_BASE_INVALIDA: 'La base de caja es un valor en pesos, de cero en adelante.',
  TN_TRASLADO_INVALIDO: 'Un traslado tiene que decir de dónde a dónde, a dos sitios distintos, y un monto mayor que cero.',
  TN_TRASLADO_CUENTA_INVALIDA: 'Un traslado apunta a una cuenta que no existe en este negocio o está desactivada.',
  TN_SIN_PERMISO_CUADRAR: 'No tienes permiso para cuadrar la caja de este negocio.',
  TN_SIN_PERMISO_TERCERO: 'No tienes permiso para registrar clientes en este negocio.',
  TN_SIN_PERMISO_SUPRIMIR_TERCERO: 'No tienes permiso para suprimir clientes en este negocio.',
  TN_SIN_PERMISO_SUPRIMIR_PRODUCTO: 'No tienes permiso para eliminar productos en este negocio.',
  TN_ELIMINAR_REQUIERE_INACTIVO: 'Desactiva el producto antes de eliminarlo.',
  TN_SIN_PERMISO_INVITAR: 'Solo el propietario puede invitar a alguien.',
  TN_SIN_PERMISO_NEGOCIO: 'Solo el propietario puede cambiar los datos del negocio.',
  TN_SIN_PERMISO_VINCULAR_BOT: 'Solo el propietario puede vincular el bot de Telegram.',
  TN_SIN_PERMISO_EQUIPO: 'Solo quien es de este negocio ve su equipo.',
  TN_SIN_PERMISO_SESIONES: 'Solo el dueño puede ver las sesiones de su negocio.',
  TN_SIN_PERMISO_DESCONECTAR: 'Solo el dueño del negocio o la plataforma pueden desconectar sesiones.',
  TN_SESION_DE_EQUIPO: 'La sesión de un miembro del equipo solo la desconecta un administrador.',
  TN_SESION_PROPIA: 'Esta es tu sesión: para salir usa el botón Salir.',
  TN_SESION_NO_EXISTE: 'Esa sesión ya no existe.',

  // --- plataforma
  TN_SOLO_PLATAFORMA: 'Esta acción es solo del equipo de plataforma.',
  TN_SIN_PERMISO_PLATAFORMA: 'Tu usuario del panel no tiene ese permiso.',
  TN_SIN_ROL_PLATAFORMA: 'No perteneces al equipo de plataforma.',
  TN_NO_ES_DE_PLATAFORMA: 'Esa persona no pertenece al equipo de plataforma.',
  TN_ADMIN_YA_TIENE_PERMISOS: 'Un administrador ya tiene todos los permisos.',
  TN_PERMISOS_PROPIOS: 'Nadie cambia sus propios permisos: pídeselo a otro administrador.',
  TN_SOLO_ADMIN_DA_ADMIN: 'Solo un administrador da acceso como administrador o cambia el de otro administrador.',
  TN_PERMISO_NO_PROPIO: 'Solo das permisos que tú también tienes: pídele los demás a un administrador.',
  TN_PERMISOS_MAYORES: 'Solo activas o desactivas a quien no tiene permisos que tú no tienes.',
  TN_ADMIN_NO_DESACTIVABLE: 'Un administrador no se desactiva desde el panel.',
  TN_NO_AUTODESACTIVAR: 'No puedes desactivarte a ti mismo.',
  TN_SANDBOX_SOLO_PLATAFORMA: 'Solo el equipo de plataforma entra a los entornos de prueba.',
  TN_NO_ES_SANDBOX: 'Ese negocio no es un entorno de prueba.',
  TN_CORREO_SIN_CUENTA: 'Ese correo no tiene cuenta todavía: pide que entre una vez a {nombre} y vuelve a intentarlo.',
  TN_CATALOGO_NO_ADMINISTRABLE: 'Ese catálogo no se administra desde el panel.',
  TN_CATALOGO_FILA_INCOMPLETA: 'La fila del catálogo necesita clave y valor.',

  // --- estado del negocio
  TN_NEGOCIO_INACTIVO: 'El negocio no está activo. Contacta {equipo}.',
  TN_NEGOCIO_NO_EXISTE: 'Ese negocio no existe.',
  TN_NEGOCIO_NO_ENCONTRADO: 'No encontramos ese negocio. Revisa el enlace o el QR.',
  TN_NEGOCIO_DUPLICADO: 'Ya tienes un negocio con ese nombre.',
  TN_COLUMNA_DE_PLATAFORMA: 'Ese dato solo lo administra el equipo de plataforma.',
  TN_ESTADO_DE_PLATAFORMA: 'El estado del negocio solo lo administra el equipo de plataforma.',
  TN_ESTADO_INVALIDO: 'Ese estado no existe.',
  TN_MOTIVO_OBLIGATORIO: 'Hay que escribir el motivo del cambio.',
  TN_SIN_CONSECUTIVO: 'El negocio no tiene numeración de comprobantes configurada.',

  // --- venta y cierre
  TN_PROVEEDOR_DESCONOCIDO: 'Ese servicio no está declarado en la tabla de proveedores.',
  TN_AVISO_DESCONOCIDO: 'Ese tipo de aviso no está en la lista que muestra el Panel.',
  TN_CUOTA_DESCONOCIDA: 'Ese consumo no está declarado en la tabla de cuotas.',
  TN_CUENTA_INEXISTENTE: 'La cuenta de pago no existe en este negocio o está desactivada.',
  TN_PRODUCTOS_INVALIDOS: 'La venta tiene productos que ya no existen, están inactivos, o cantidades inválidas.',
  TN_VENTA_LINEAS_FUERA_DE_RANGO: 'La venta debe tener entre 1 y 100 líneas.',
  TN_PAGOS_FUERA_DE_RANGO: 'Una venta admite entre 1 y 5 pagos.',
  TN_PAGO_CUENTA_INVALIDA: 'Uno de los pagos apunta a una cuenta que no existe en este negocio, o su monto no es positivo.',
  TN_PAGOS_NO_SUMAN: 'Los pagos tienen que sumar exactamente el total de la venta.',
  TN_DESCUENTO_NO_PERMITIDO: 'Ese descuento no está habilitado para tu rol.',
  TN_DESCUENTO_INEXISTENTE: 'Ese descuento no existe en este negocio: recarga la página.',
  TN_DESCUENTO_FUERA_DE_LUGAR: 'Ese descuento no se usa ahí: revisa en Configuración dónde se usa.',
  TN_DESCUENTO_DESACTIVADO: 'Ese descuento ya estaba desactivado: quítalo de la venta.',
  TN_DESCUENTO_DOBLE: 'Una venta lleva descuento en sus productos o a la venta, no ambos.',
  TN_DESCUENTO_INMUTABLE: 'Un descuento eliminado no se recupera: crea uno nuevo.',
  TN_EFECTIVO_PROTEGIDO: 'La cuenta de efectivo no se desactiva ni se elimina: el cuadre la cuenta.',
  TN_CUENTA_CON_DIA_ABIERTO: 'Esta cuenta tiene movimiento en días sin cerrar: cierra esos días y vuelve a intentarlo.',
  TN_DESCUENTO_INVALIDO: 'El descuento está fuera de rango: revisa el porcentaje o el valor.',
  TN_PROMOCION_SUPERPUESTA: 'Ese descuento automático se cruza con otro en los mismos productos y fechas: cambia uno de los dos.',
  TN_PROMOCION_FUERA_DE_VIGENCIA: 'Ese descuento automático no estaba vigente en el momento de la venta: quítalo de la venta.',
  TN_DESCUENTO_NO_APLICA: 'Ese descuento no cubre ese producto: quítalo de la línea.',
  TN_ALCANCE_SOLO_EN_PRODUCTO: 'Un descuento para ciertos productos no se usa a la venta: desmarca «A la venta (F8)».',
  TN_SIN_PERMISO_CONFIGURAR: 'Solo el propietario configura los descuentos.',
  TN_GASTO_SIN_ORIGEN: 'Hay que decir de dónde salió la plata: una cuenta del negocio o la caja general.',
  TN_GASTO_CUENTA_INVALIDA: 'La cuenta del gasto no existe en este negocio o está desactivada.',
  TN_GASTO_SIN_DESCRIPCION: 'Escribe qué se compró: sin documento del proveedor es lo único que lo dice.',
  TN_IMPUESTO_SIN_NOMBRE: 'El impuesto que no es IVA ni INC tiene que decir cómo se llama.',
  TN_FECHA_VENTA_FUERA_DE_RANGO: 'La fecha de la venta está fuera del rango permitido.',
  TN_ANULACION_SIN_MOTIVO: 'Para anular hay que escribir el motivo.',
  TN_ANULACION_ALTERA_DATOS: 'La anulación no puede cambiar los datos del comprobante.',
  TN_COMPROBANTE_NO_ANULABLE: 'Ese comprobante no existe o ya estaba anulado.',
  TN_COMPROBANTE_ANULADO_INMUTABLE: 'Un comprobante anulado no se puede modificar.',
  TN_VENTA_INMUTABLE: 'Un comprobante emitido no se puede modificar: solo anular.',
  TN_COMPROBANTE_NO_SE_BORRA: 'Un comprobante no se borra: se anula, con su motivo.',

  // --- catálogo
  TN_SIN_PERMISO_CATALOGO: 'Solo el propietario puede importar el catálogo.',
  TN_IMPORTACION_VACIA: 'El archivo no trae ninguna fila para importar.',
  TN_IMPORTACION_MUY_GRANDE: 'La importación admite hasta 2000 filas por archivo. Pártelo en dos.',
  TN_SKU_DUPLICADO: 'El archivo trae el mismo SKU más de una vez. Deja uno solo y vuelve a subirlo.',
  TN_FILA_INVALIDA: 'Una fila del archivo no tiene sku, nombre o precio válidos.',
  TN_IMPUESTO_DESCONOCIDO:
    'Ese impuesto no existe en tu configuración. Créalo en Configuración → Impuestos, o corrige el archivo. No se importó nada.',

  // --- recetas
  TN_SIN_PERMISO_RECETA: 'Solo el propietario puede cambiar las recetas.',
  TN_PRODUCTO_NO_ENCONTRADO: 'Ese producto no existe en este negocio.',
  TN_RECETA_INVALIDA: 'No pudimos leer las líneas de la receta. Vuelve a intentarlo.',
  TN_INSUMO_REPETIDO: 'El mismo insumo aparece dos veces en la receta. Déjalo una sola vez con la cantidad total.',
  TN_INSUMO_INVALIDO:
    'Uno de los insumos no es de este negocio o su cantidad no sirve. La receta quedó como estaba.',

  // --- gastos
  TN_GASTO_CUFE_DUPLICADO: 'Ese documento del proveedor ya estaba registrado.',
  TN_GASTO_ANULADO: 'Ese gasto está anulado: ya no se corrige ni se vuelve a anular.',
  TN_GASTO_SIN_TOTAL: 'Escribe cuánto pagaste.',
  TN_DIA_CERRADO_CORREGIR: 'El día de ese gasto ya se cerró. Reábrelo en Cierre del día para corregirlo.',
  TN_PROVEEDOR_SIN_NOMBRE: 'Escribe el nombre o la razón social del proveedor.',
  TN_EVIDENCIA_FUERA_DEL_NEGOCIO: 'El archivo no corresponde a este negocio.',

  // --- identidad de personas
  TN_DOCUMENTO_INVALIDO: 'El número de documento no corresponde al tipo elegido.',
  TN_CELULAR_INVALIDO: 'El celular debe tener 10 dígitos.',
  TN_CELULAR_OBLIGATORIO: 'Escribe el celular.',
  TN_CORREO_INVALIDO: 'El correo no es válido.',
  TN_CORREO_OBLIGATORIO: 'Escribe el correo.',
  TN_NOMBRE_OBLIGATORIO: 'Escribe el nombre.',
  TN_APELLIDO_OBLIGATORIO: 'Escribe el apellido.',
  TN_NOMBRES_OBLIGATORIOS: 'Escribe los nombres y apellidos, mínimo dos letras cada uno.',
  TN_NOMBRE_MUY_LARGO: 'El nombre no puede pasar de 80 caracteres.',
  TN_APELLIDO_MUY_LARGO: 'El apellido no puede pasar de 80 caracteres.',
  TN_CARGO_MUY_LARGO: 'El cargo no puede pasar de 60 caracteres.',
  TN_RAZON_SOCIAL_OBLIGATORIA: 'La razón social es obligatoria para una empresa.',
  TN_TIPO_PERSONA_INVALIDO: 'El tipo de persona no es válido.',
  // La obligación legal de una empresa se nombra: es lo que explica el rechazo.
  // eslint-disable-next-line no-restricted-syntax
  TN_JURIDICA_NO_DISPONIBLE: 'Por ahora solo se pueden crear negocios de persona natural: una empresa está obligada a facturación electrónica y {nombre} todavía no la emite.',
  TN_CONSENTIMIENTO_OBLIGATORIO: 'Para crear la cuenta hay que autorizar el tratamiento de datos.',
  TN_NOMBRE_NEGOCIO_INVALIDO: 'El nombre del negocio no es válido.',
  TN_NOMBRE_LARGO_INVALIDO: 'El nombre debe tener entre 3 y 80 caracteres.',

  // --- invitaciones y equipo
  TN_INVITACION_INVALIDA: 'La invitación no es válida o ya venció.',
  TN_INVITACION_DE_OTRO: 'Esa invitación es para otro correo.',
  TN_ROL_INVALIDO: 'Ese rol no existe.',
};

/** Frase por código técnico de Postgres o PostgREST, cuando no hay código de dominio. */
const MENSAJES_TECNICOS: Record<string, string> = {
  '23505': 'Eso ya estaba registrado.',
  '23502': 'Falta un dato obligatorio.',
  '23503': 'Ese dato depende de otro que no existe.',
  '23514': 'Un dato no cumple una regla del negocio.',
  '22P02': 'Un dato tiene un formato que no se entiende.',
  '22007': 'Una fecha tiene un formato que no se entiende.',
  '42501': 'No tienes permiso para esto.',
  '42P10': 'La aplicación pidió una escritura que la base no puede resolver. Recarga la página.',
  '42703': 'La aplicación pidió un dato que ya no existe. Recarga la página.',
  '57014': 'La consulta tardó demasiado. Vuelve a intentar.',
  '53300': 'El servidor está saturado. Vuelve a intentar en un momento.',
  '40001': 'Otra persona cambió lo mismo al mismo tiempo. Vuelve a intentar.',
  '40P01': 'Dos operaciones se cruzaron. Vuelve a intentar.',
  PGRST301: 'Tu sesión venció. Vuelve a entrar.',
  PGRST302: 'Tu sesión no es válida. Vuelve a entrar.',
  PGRST002: 'El servidor está reiniciando. Vuelve a intentar en un momento.',
  PGRST205: 'La aplicación pidió algo que ya no existe. Recarga la página.',
  PGRST116: 'Se esperaba un solo resultado y llegaron varios.',
};

const GENERICO = 'No pudimos completar la acción. Vuelve a intentar.';
const SIN_RED = 'Sin conexión con el servidor.';

/** El prefijo `TN_*` del mensaje del servidor, si lo trae. */
export function codigoDominioDe(mensaje: string | undefined): string | undefined {
  const m = /^(TN_[A-Z0-9_]+)(?::|$)/.exec((mensaje ?? '').trim());
  return m?.[1];
}

/**
 * La frase del formulario público del QR, que habla por HTTP y no por supabase-js: las reglas de
 * identidad llegan con su código `TN_*` en el cuerpo, y los fallos de la Edge Function solo status.
 */
export function fraseDelRegistroPublico(status: number, mensajeServidor?: string): string {
  if (codigoDominioDe(mensajeServidor)) return traducirError({ message: mensajeServidor, status });
  if (status === 0) return SIN_RED;
  if (status === 403) return 'No pudimos verificar que no eres un robot. Recarga la página e intenta de nuevo.';
  if (status === 404) return 'Ese enlace de registro no es válido. Pide al negocio su QR.';
  if (status === 429) return 'Demasiados intentos seguidos. Espera un momento.';
  if (status === 503) return 'El registro no está disponible en este momento. Inténtalo más tarde.';
  if (status >= 500) return 'El servidor no está respondiendo. Intenta de nuevo.';
  return 'Revisa los datos e intenta de nuevo.';
}

function conMarca(frase: string): string {
  return frase.replaceAll('{nombre}', instalacion.nombre).replaceAll('{equipo}', instalacion.textos.equipo);
}

/**
 * La frase final para la persona. Nunca devuelve texto que vino del servidor: si no reconoce el
 * código, devuelve el genérico.
 */
export function traducirError(bruto: {
  code?: string | null;
  message?: string | null;
  status?: number;
}): string {
  const dominio = codigoDominioDe(bruto.message ?? undefined);
  const deDominio = dominio ? MENSAJES_DOMINIO[dominio] : undefined;
  if (deDominio) return conMarca(deDominio);
  const deTecnico = bruto.code ? MENSAJES_TECNICOS[bruto.code] : undefined;
  if (deTecnico) return deTecnico;
  if (bruto.status === 0) return SIN_RED;
  if (bruto.status === 429) return 'Demasiadas peticiones seguidas. Espera un momento.';
  if ((bruto.status ?? 0) >= 500) return 'El servidor no está respondiendo. Vuelve a intentar.';
  return GENERICO;
}
